import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  Button,
  Dropdown,
  GenericStatusComponent,
  Input,
  Loader,
  Modal,
  Snackbar
} from '@paysense-india-services/elevate-web'

import { EXTERNAL_ROUTES, ROUTES } from '@/constants/routes'

import { useProductConfig } from '@/contexts/ProductConfigContext'

import useFetch from '@/hooks/useFetch'

import { generateUuidV4 } from '@/utils/uuidV4'

import Header from '@/components/Header'
import Logo from '@/components/Logo'
import OtpEntry from '@/components/OtpEntry'
import { DEFAULT_OTP_RESEND_AFTER_SECONDS } from '@/components/OtpEntry/constants'
import PageContainer from '@/components/PageContainer'

import { GREEN_TICK } from '@/assets/images'

import { PRODUCT_OPERATIONS } from '@/config/productApi'
import { PRODUCT_SOURCES } from '@/config/products'

import {
  CATEGORY_OPTIONS,
  EMAIL_REGEX,
  ERROR_MESSAGES,
  getSubcategoryOptions,
  INITIAL_FORM,
  LOAN_SERVICE_PROVIDER_OPTIONS,
  LODGE_OTP_MESSAGES,
  PHONE_REGEX,
  SUCCESS_MESSAGES
} from './constants'

import styles from './index.module.scss'

const LodgeAComplaintPage = () => {
  const navigate = useNavigate()
  const { config } = useProductConfig()
  const [form, setForm] = useState(INITIAL_FORM)
  const [submitted, setSubmitted] = useState(false)
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpResendAfterSeconds, setOtpResendAfterSeconds] = useState(
    DEFAULT_OTP_RESEND_AFTER_SECONDS
  )
  const [otpEntryKey, setOtpEntryKey] = useState(0)
  const prevCategory = useRef(form.category)

  useEffect(() => {
    if (config.source !== PRODUCT_SOURCES.CL) {
      navigate(`${ROUTES.LODGE_COMPLAINT}?source=${PRODUCT_SOURCES.CL}`, { replace: true })
    }
  }, [config.source, navigate])

  const { loading, apiRequest } = useFetch()
  const { loading: sendOtpLoading, apiRequest: callSendOtp } = useFetch()

  const formBusy = loading || sendOtpLoading

  const isFormComplete =
    !!form.loanServiceProvider &&
    !!form.category &&
    !!form.subcategory &&
    !!form.name?.trim() &&
    !!form.email?.trim() &&
    !!form.mobileNumber?.trim() &&
    !!form.issueDescription?.trim()

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

  useEffect(() => {
    if (prevCategory.current !== form.category) {
      prevCategory.current = form.category
      setForm(prev => ({ ...prev, subcategory: '' }))
    }
  }, [form.category])

  const subcategoryOptions = getSubcategoryOptions(form.category)

  const showValidationError = message => {
    Snackbar.show(message, { type: 'error' })
    return false
  }

  const validationRules = [
    [() => !form.loanServiceProvider, ERROR_MESSAGES.EMPTY_LOAN_SERVICE_PROVIDER],
    [() => !form.category, ERROR_MESSAGES.EMPTY_CATEGORY],
    [() => !form.subcategory, ERROR_MESSAGES.EMPTY_SUBCATEGORY],
    [() => !form.name?.trim(), ERROR_MESSAGES.EMPTY_NAME],
    [() => !form.email?.trim(), ERROR_MESSAGES.EMPTY_EMAIL],
    [() => !EMAIL_REGEX.test(form.email), ERROR_MESSAGES.INVALID_EMAIL],
    [() => !form.mobileNumber?.trim(), ERROR_MESSAGES.EMPTY_MOBILE_NUMBER],
    [() => !PHONE_REGEX.test(form.mobileNumber.trim()), ERROR_MESSAGES.INVALID_MOBILE_NUMBER],
    [() => !form.issueDescription?.trim(), ERROR_MESSAGES.EMPTY_ISSUE_DESCRIPTION]
  ]

  const validate = () => {
    const failed = validationRules.find(([check]) => check())
    if (failed) return showValidationError(failed[1])
    return true
  }

  const resetForm = () => setForm({ ...INITIAL_FORM })

  const sendComplaintEmailOtpFlow = ({
    onSuccess,
    fallbackErrorMessage = LODGE_OTP_MESSAGES.SEND_FAILED
  } = {}) => {
    callSendOtp(
      PRODUCT_OPERATIONS.SEND_COMPLAINT_OTP,
      { email: form.email.trim() },
      {
        onSuccess: result => {
          setOtpResendAfterSeconds(result.resendAfterSeconds ?? DEFAULT_OTP_RESEND_AFTER_SECONDS)
          setOtpEntryKey(k => k + 1)
          onSuccess?.()
          Snackbar.show(LODGE_OTP_MESSAGES.SENT, { type: 'success' })
        },
        onError: err => {
          Snackbar.show(err?.parsedMessage || fallbackErrorMessage, { type: 'error' })
        }
      }
    )
  }

  const openOtpModal = () => {
    if (!validate()) return
    sendComplaintEmailOtpFlow({ onSuccess: () => setShowOtpModal(true) })
  }

  const closeOtpModal = () => setShowOtpModal(false)

  const submitWithOtp = (otp, setOtpError) => {
    apiRequest(
      PRODUCT_OPERATIONS.RAISE_COMPLAINT,
      {
        email: form.email.trim(),
        otp,
        idempotencyKey: generateUuidV4(),
        ticket: {
          service_provider: form.loanServiceProvider,
          name: form.name,
          description: form.issueDescription.trim(),
          category: form.category,
          sub_category: form.subcategory,
          phone: form.mobileNumber?.trim() || '',
          metadata: {}
        }
      },
      {
        onSuccess: result => {
          if (!result?.ok) {
            const message = result?.message ?? ERROR_MESSAGES.SUBMIT_FAILED
            setOtpError?.(message)
            Snackbar.show(message, { type: 'error' })
            return
          }

          closeOtpModal()
          resetForm()
          setSubmitted(true)
        },
        onError: err => {
          const message = err?.parsedMessage || ERROR_MESSAGES.SUBMIT_FAILED
          setOtpError?.(message)
          Snackbar.show(message, { type: 'error' })
        }
      }
    )
  }

  const handleResendOtp = () => {
    sendComplaintEmailOtpFlow({ fallbackErrorMessage: LODGE_OTP_MESSAGES.RESEND_FAILED })
  }

  return (
    <>
      <Header leftComponent={Logo} />
      <div className={`ew-d-flex ew-flex-column ${styles.contentCard}`}>
        <div className={styles.headerBlock}>
          <h1 className="ew-typography-huge ew-fw-bold ew-color-primary ew-mb-8">
            Lodge a complaint
          </h1>
          <p className="ew-typography-moderate ew-color-primary ew-fw-regular ew-mb-24">
            Raise a concern and we’ll help resolve it
          </p>
        </div>
        <div className="appLayout ew-mt-12">
          {submitted ? (
            <PageContainer className="ew-d-flex ew-flex-column">
              <GenericStatusComponent
                className="ew-mx-auto ew-shadow-none"
                onPrimaryAction={() => {
                  globalThis.location.href = EXTERNAL_ROUTES.WEBSITE
                }}
                primaryBtnText="Okay, Got it"
                statusIcon={GREEN_TICK}
                subtitle={SUCCESS_MESSAGES.COMPLAINT_SUBMITTED_SUBTITLE}
                title={SUCCESS_MESSAGES.COMPLAINT_SUBMITTED_TITLE}
              />
            </PageContainer>
          ) : (
            <PageContainer className="ew-d-flex ew-flex-column ew-p-32">
              <div className={styles.formWrapper}>
                {sendOtpLoading ? (
                  <div aria-busy aria-label="Sending OTP" className={styles.formOverlay}>
                    <Loader color="brand" loadingText="Sending OTP..." pageLoader size="large" />
                  </div>
                ) : null}
                <div className={styles.form} data-form="lodge-complaint">
                  <Dropdown
                    disabled={formBusy}
                    label="Loan Service Provider"
                    onChange={label =>
                      setField('loanServiceProvider', label === 'Select' ? '' : label)
                    }
                    options={LOAN_SERVICE_PROVIDER_OPTIONS}
                    value={form.loanServiceProvider || 'Select'}
                  />
                  <Dropdown
                    disabled={formBusy}
                    label="Category"
                    onChange={label =>
                      setField('category', label === 'Select main category' ? '' : label)
                    }
                    options={CATEGORY_OPTIONS}
                    value={form.category || 'Select main category'}
                  />
                  <Dropdown
                    disabled={formBusy || !form.category}
                    label="Subcategory"
                    onChange={label =>
                      setField('subcategory', label === 'Select sub category' ? '' : label)
                    }
                    options={subcategoryOptions}
                    value={form.subcategory || 'Select sub category'}
                  />
                  <Input
                    disabled={formBusy}
                    label="Name"
                    onChange={val => setField('name', val)}
                    value={form.name}
                  />
                  <Input
                    disabled={formBusy}
                    label="Registered Email"
                    onChange={val => setField('email', val)}
                    type="email"
                    validator={val =>
                      val && !EMAIL_REGEX.test(val) ? ERROR_MESSAGES.INVALID_EMAIL : ''
                    }
                    value={form.email}
                  />
                  <Input
                    disabled={formBusy}
                    inputMode="numeric"
                    label="Registered Phone number"
                    onChange={val => setField('mobileNumber', val)}
                    onChangeValidator={val => /^\d{0,10}$/.test(val)}
                    prefix="+91"
                    type="text"
                    validator={val =>
                      val && !PHONE_REGEX.test(val) ? ERROR_MESSAGES.INVALID_MOBILE_NUMBER : ''
                    }
                    value={form.mobileNumber}
                  />

                  <div>
                    <textarea
                      className={styles.textarea}
                      disabled={formBusy}
                      id="issue-details"
                      onChange={e => setField('issueDescription', e.target.value)}
                      placeholder="Issue details"
                      rows={5}
                      value={form.issueDescription}
                    />
                  </div>
                  <Button
                    className="ew-mt-8"
                    fullWidth
                    isDisabled={formBusy || !isFormComplete}
                    onClick={openOtpModal}
                    size="medium"
                    type="default"
                    variant="brand"
                  >
                    Submit
                  </Button>
                </div>
              </div>
              <Snackbar />
            </PageContainer>
          )}
        </div>
      </div>

      {showOtpModal ? (
        <Modal
          className={styles.otpModal}
          onCrossClick={closeOtpModal}
          onOverlayClick={closeOtpModal}
          show={showOtpModal}
          showCross
        >
          <OtpEntry
            incompleteOtpMessage={ERROR_MESSAGES.EMPTY_OTP}
            isResendLoading={sendOtpLoading}
            key={otpEntryKey}
            onResend={handleResendOtp}
            onVerify={submitWithOtp}
            resendAfterSeconds={otpResendAfterSeconds}
            subtitle={`We just sent an OTP to your email ${form.email.trim()}`}
            title="Enter OTP"
            verifyButtonText="Confirm"
            verifyLoading={loading}
          />
        </Modal>
      ) : null}
    </>
  )
}

export default LodgeAComplaintPage
